import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

// Utility function for delay
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Speech Synthesis Function
const speak = (text) => {
  const synth = window.speechSynthesis;
  if (synth.speaking) {
    console.error('speechSynthesis.speaking');
    return;
  }
  const utterThis = new SpeechSynthesisUtterance(text);
  synth.speak(utterThis);
};

const SpeechToTextForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    comment: '',
  });

  const [isRecording, setIsRecording] = useState(false);
  const [currentField, setCurrentField] = useState(null);
  const [error, setError] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const API_KEY = "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";

  const OPENAI_API_URL = 'https://api.openai.com/v1/audio/transcriptions';
  const OPENAI_API_KEY = API_KEY;
  const throttleDuration = 5000; // 5 seconds
  const lastRequestRef = useRef(0);

  const handleFocus = async (fieldName) => {
    if (isRecording || isTranscribing) return;

    const now = Date.now();
    if (now - lastRequestRef.current < throttleDuration) {
      setError('Please wait a moment before making another request.');
      return;
    }
    lastRequestRef.current = now;

    setError(null);
    setCurrentField(fieldName);
    speak(`Please say your ${fieldName.replace(/([A-Z])/g, ' $1').toLowerCase()}`);

    try {
      await startRecording(fieldName);
    } catch (err) {
      console.error('Recording Error:', err);
      setError('Failed to record audio.');
    }
  };

  const startRecording = async (fieldName) => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError('Microphone access is not supported in your browser.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstart = () => {
        setIsRecording(true);
      };

      mediaRecorderRef.current.onstop = async () => {
        setIsRecording(false);
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        await transcribeAudio(audioBlob, currentField);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorderRef.current.start();
    } catch (err) {
      console.error('Error accessing microphone:', err);
      setError('Unable to access the microphone.');
    }
  };

  const transcribeAudio = async (audioBlob, fieldName) => {
    setIsTranscribing(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', audioBlob, 'audio.wav');
    formData.append('model', 'whisper-1');

    const maxRetries = 2;
    let attempt = 0;
    let success = false;
    let transcription = '';

    while (attempt < maxRetries && !success) {
      try {
        const response = await axios.post(OPENAI_API_URL, formData, {
          headers: {
            Authorization: `Bearer ${OPENAI_API_KEY}`,
            'Content-Type': 'multipart/form-data',
          },
        });

        transcription = response.data.text.trim();
        success = true;
      } catch (err) {
        if (err.response && err.response.status === 429) {
          attempt += 1;
          const waitTime = 2 ** attempt * 1000;
          console.warn(`Rate limit hit. Retrying in ${waitTime / 1000} seconds...`);
          await delay(waitTime);
        } else if (err.response && err.response.status === 401) {
          console.error('Authorization Error: Invalid API Key');
          setError('Invalid API Key. Please check your OpenAI API Key.');
          break;
        } else {
          console.error('Transcription Error:', err);
          setError('Failed to transcribe audio.');
          break;
        }
      }
    }

    if (success) {
      setFormData((prev) => ({
        ...prev,
        [fieldName]: transcription,
      }));
      setIsTranscribing(false);
    } else {
      setError('Unable to transcribe. Please try again later.');
      setIsTranscribing(false);
    }
  };

  // Adding keypress functionality for starting and stopping recording
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'R' || e.key === 'r') {
        // Start recording
        if (!isRecording) {
          startRecording(currentField);  // Start recording for the current field
        }
      }

      if (e.key === 'P' || e.key === 'p') {
        // Stop recording
        if (isRecording && mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isRecording, currentField]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
    // Part 2: Additional Functionality

    const [transcription, setTranscription] = useState('');
    const [recording, setRecording] = useState(false);
    const [audioBlobUrl, setAudioBlobUrl] = useState(null);
    const [summary, setSummary] = useState('');
    const recognitionRef = useRef(null);
    const audioChunksRef2 = useRef([]);
    const startTimeRef = useRef(null);

    useEffect(() => {
      if (!('webkitSpeechRecognition' in window)) {
        alert('WebSpeech API is not supported in this browser');
        return;
      }

      const SpeechRecognition = window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        let interimTranscription = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            setTranscription((prev) => prev + event.results[i][0].transcript + ' ');
          } else {
            interimTranscription += event.results[i][0].transcript;
          }
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Recognition error:', event);
        alert('Error with Speech Recognition. Please try again.');
      };
    }, []);

    const startRecording2 = () => {
      setRecording(true);
      audioChunksRef2.current = [];
      startTimeRef.current = new Date();
      recognitionRef.current.start();
    };

    const stopRecording2 = () => {
      setRecording(false);
      recognitionRef.current.stop();
      navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorder.ondataavailable = (event) => {
          audioChunksRef2.current.push(event.data);
        };

        mediaRecorder.onstop = () => {
          const audioBlob = new Blob(audioChunksRef2.current, { type: 'audio/wav' });
          const audioUrl = URL.createObjectURL(audioBlob);
          setAudioBlobUrl(audioUrl);
        };

        mediaRecorder.start();
        setTimeout(() => mediaRecorder.stop(), 1000);
      });
    };

    const handleSummarize = () => {
      if (!transcription) {
        alert('No transcription available for summarization.');
        return;
      }

      axios
        .post(
          'https://api.openai.com/v1/completions',
          {
            prompt: `Summarize the following transcription: ${transcription}`,
            model: 'gpt-4',
            max_tokens: 100,
          },
          {
            headers: {
              Authorization: `Bearer ${process.env.REACT_APP_OPENAI_API_KEY}`,
            },
          }
        )
        .then((response) => {
          setSummary(response.data.choices[0].text);
        })
        .catch((err) => {
          console.error('Error summarizing transcription:', err);
        });
    };

    useEffect(() => {
      const handleKeyPress = (event) => {
        if (event.key === 's' || event.key === 'S') {
          const userChoice = prompt("Do you want to record your input or type manually? Type 'R' for recording or 'T' for typing.");
    
          if (userChoice && userChoice.toLowerCase() === 'r') {
            // Start recording
            startRecording2();
          } else if (userChoice && userChoice.toLowerCase() === 't') {
            // Manually type; focus on the current input field
            if (currentField) {
              document.getElementById(currentField).focus();
            } else {
              alert("Please click on a field to focus before typing.");
            }
          } else {
            alert('Invalid choice! Press "R" for recording or "T" for typing.');
          }
        } else if (event.key === 'k' || event.key === 'K') {
          stopRecording2();
        } else if (event.key === 't' || event.key === 'T') {
          handleSummarize();
        }
      };
    
      window.addEventListener('keydown', handleKeyPress);
    
      return () => {
        window.removeEventListener('keydown', handleKeyPress);
      };
    }, [currentField, transcription]);
    

    return (
      <div style={styles.container}>
        <h1>Speech to Text Form</h1>
        <form style={styles.form}>
        <div style={styles.fieldContainer}>
          <label htmlFor="firstName" style={styles.label}>
            First Name
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onFocus={() => handleFocus('firstName')}
            onChange={handleChange}
            style={styles.input}
            placeholder="Click here and speak your first name"
            disabled={isRecording || isTranscribing}
          />
          {currentField === 'firstName' && isRecording && (
            <span style={styles.status}>Recording...</span>
          )}
        </div>

        <div style={styles.fieldContainer}>
          <label htmlFor="lastName" style={styles.label}>
            Last Name
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onFocus={() => handleFocus('lastName')}
            onChange={handleChange}
            style={styles.input}
            placeholder="Click here and speak your last name"
            disabled={isRecording || isTranscribing}
          />
          {currentField === 'lastName' && isRecording && (
            <span style={styles.status}>Recording...</span>
          )}
        </div>

        <div style={styles.fieldContainer}>
          <label htmlFor="comment" style={styles.label}>
            Comment
          </label>
          <textarea
            id="comment"
            name="comment"
            value={formData.comment}
            onFocus={() => handleFocus('comment')}
            onChange={handleChange}
            style={styles.textarea}
            placeholder="Click here and speak your comment"
            disabled={isRecording || isTranscribing}
          ></textarea>
          {currentField === 'comment' && isRecording && (
            <span style={styles.status}>Recording...</span>
          )}
        </div>
        {error && <p style={styles.error}>{error}</p>}
      </form>

        <div style={styles.transcriptionContainer}>
          <h2>Transcription and Summarization</h2>
          <div style={styles.controls}>
            {recording ? (
              <button onClick={stopRecording2} style={styles.button}>
                Stop Recording
              </button>
            ) : (
              <button onClick={startRecording2} style={styles.button}>
                Start Recording
              </button>
            )}
          </div>
          <div style={styles.transcriptionBox}>
            <h3>Transcription:</h3>
            <p>{transcription || 'Transcription will appear here...'}</p>
          </div>
          {audioBlobUrl && (
            <div style={styles.audioPlayer}>
              <h3>Recorded Audio:</h3>
              <audio controls src={audioBlobUrl} />
            </div>
          )}
          <button onClick={handleSummarize} style={styles.button}>
            Summarize Transcription
          </button>
          {summary && (
            <div style={styles.summaryBox}>
              <h3>Summary:</h3>
              <p>{summary}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const styles = {
    container: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px',
      fontFamily: 'Arial, sans-serif',
    },
    form: {
      marginBottom: '20px',
    },
    fieldContainer: {
      marginBottom: '10px',
    },
    label: {
      display: 'block',
      fontWeight: 'bold',
      marginBottom: '5px',
    },
    input: {
      width: '100%',
      padding: '10px',
      fontSize: '16px',
      borderRadius: '5px',
      border: '1px solid #ccc',
    },
    textarea: {
      width: '100%',
      padding: '10px',
      fontSize: '16px',
      borderRadius: '5px',
      border: '1px solid #ccc',
      height: '100px',
    },
    button: {
      padding: '10px 20px',
      backgroundColor: '#007bff',
      color: '#fff',
      border: 'none',
      borderRadius: '5px',
      cursor: 'pointer',
      fontSize: '16px',
    },
    status: {
      display: 'inline-block',
      marginLeft: '10px',
      fontWeight: 'bold',
      color: '#007bff',
    },
    error: {
      color: 'red',
    },
    transcriptionContainer: {
      marginTop: '20px',
    },
    controls: {
      marginBottom: '10px',
    },
    transcriptionBox: {
      marginBottom: '10px',
      padding: '10px',
      border: '1px solid #ccc',
      borderRadius: '5px',
    },
    audioPlayer: {
      marginTop: '10px',
    },
    summaryBox: {
      marginTop: '10px',
    },
  };

  export default SpeechToTextForm;
